import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Calendar, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { base44 } from '@/api/base44Client';

const BOOK_URL = '/book-appointment';

export default function FloatingCTA({ page = 'unknown' }) {
  const [desktopVisible, setDesktopVisible] = useState(false);
  const [desktopDismissed, setDesktopDismissed] = useState(false);
  const [mobileVisible, setMobileVisible] = useState(false);
  const [mobileDismissed, setMobileDismissed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Detect mobile menu open state by watching for the menu DOM element
  useEffect(() => {
    const observer = new MutationObserver(() => {
      const menuOpen = !!document.querySelector('.lg\\:hidden.bg-white.border-t.border-slate-200');
      setMobileMenuOpen(menuOpen);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  // Both mobile and desktop: show after 40% scroll so they don't compete with other popups
  useEffect(() => {
    const handleScroll = () => {
      const scrollPct = window.scrollY / (document.documentElement.scrollHeight - window.innerHeight) * 100;
      if (scrollPct > 40) {
        if (!desktopDismissed) setDesktopVisible(true);
        if (!mobileDismissed) setMobileVisible(true);
      }
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [desktopDismissed, mobileDismissed]);

  const track = (eventName, extra = {}) => {
    try {
      base44.analytics.track({
        eventName,
        properties: { page, ...extra }
      });
    } catch (_) {}
  };

  const handleCTAClick = (action, location) => {
    track('appointment_intent', { action, location, intent_score: action === 'book' ? 'high' : 'medium' });
    if (action === 'call') {
      window.location.href = 'tel:+17272286846';
    } else {
      window.open(BOOK_URL, '_blank');
    }
  };

  const dismissDesktop = () => {
    setDesktopDismissed(true);
    setDesktopVisible(false);
    track('floating_cta_dismiss', { location: 'desktop' });
  };

  const dismissMobile = () => {
    setMobileDismissed(true);
    setMobileVisible(false);
    track('floating_cta_dismiss', { location: 'mobile' });
  };

  return (
    <>
      {/* ── Mobile floating bubble (hidden when mobile menu is open) ── */}
      <AnimatePresence>
        {mobileVisible && !mobileDismissed && !mobileMenuOpen &&
        <motion.div
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="md:hidden fixed bottom-6 left-4 z-40 flex flex-col items-start gap-2">
          
            {/* Dismiss */}
            <button
            onClick={dismissMobile}
            className="w-5 h-5 rounded-full bg-slate-400 hover:bg-slate-500 flex items-center justify-center self-start ml-0.5"
            aria-label="Dismiss">
            
              <X className="w-3 h-3 text-white" />
            </button>

            <div className="flex gap-2">
              {/* Call bubble */}
              <a
              href="tel:+17272286846"
              onClick={() => track('appointment_intent', { action: 'call', location: 'mobile_bubble', intent_score: 'medium' })}
              className="w-14 h-14 rounded-full bg-slate-800 shadow-lg flex items-center justify-center hover:bg-slate-700 transition-colors"
              aria-label="Call (727) 228-6846">
              
                <Phone className="w-6 h-6 text-white" />
              </a>

              {/* Book bubble */}
              <button
              onClick={() => handleCTAClick('book', 'mobile_bubble')}
              className="w-14 h-14 rounded-full bg-amber-600 shadow-lg flex items-center justify-center hover:bg-amber-700 transition-colors"
              aria-label="Book appointment">
              
                <Calendar className="w-6 h-6 text-white" />
              </button>
            </div>
          </motion.div>
        }
      </AnimatePresence>

      {/* ── Desktop floating card (scroll-triggered) ── */}
      <AnimatePresence>
        {desktopVisible &&
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="hidden md:flex fixed bottom-6 right-6 z-50 gap-3 items-center bg-white shadow-2xl rounded-2xl p-4 border-2 border-amber-200">
          
            <button
            onClick={dismissDesktop}
            className="absolute -top-2 -right-2 bg-slate-100 hover:bg-slate-200 rounded-full p-1 transition-colors"
            aria-label="Dismiss">
            
              <X className="w-4 h-4 text-slate-600" />
            </button>

            <div>
              <p className="text-sm font-semibold text-slate-800">1000+ Smiles Transformed — Yours Is Next</p>
              <p className="text-xs text-slate-600">Expert care · Same-day availability</p>
            </div>

            <div className="flex gap-2">
              <Button
              onClick={() => handleCTAClick('call', 'desktop_float')}
              className="bg-amber-600 hover:bg-amber-700 flex items-center gap-2">
              
                <Phone className="w-4 h-4" />
                Call Now
              </Button>
              <Button
              asChild
              className="bg-slate-800 hover:bg-slate-900 flex items-center gap-2">
              
                <a href={BOOK_URL}>
                  <Calendar className="w-4 h-4" />
                  Book Online
                </a>
              </Button>
            </div>
          </motion.div>
        }
      </AnimatePresence>
    </>);

}